import { useMapLayerMouseEvent } from '@hooks/useMapLayerMouseEvent';
import { uniqBy } from 'lodash';
import { GeoJSONFeature, MapEventOf } from 'mapbox-gl';
import { useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { LayerNameEnum } from '@/types/map.types';
import { MenuItems } from '@/types/menu.types';

export interface MapFeatureSelectionProps {
  layers: LayerNameEnum[];
  featureKey: string; // Key from feature.properties used to identify distinct features (e.g. 'code', 'value')
  featureLabelKey: string;
  onFeatureSelect: (feature: GeoJSONFeature) => void;
  onClearSelection?: () => void;
}

interface UseMapFeatureSelectionReturn {
  items: MenuItems | null;
  clickPosition: { x: number; y: number } | null;
}

export function useMapFeatureSelection({
  layers,
  featureKey,
  featureLabelKey,
  onFeatureSelect,
  onClearSelection,
}: Readonly<MapFeatureSelectionProps>): UseMapFeatureSelectionReturn {
  const { current: mapboxMap } = useMap();
  const [items, setItems] = useState<MenuItems | null>(null);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedFeatureKey, setSelectedFeatureKey] = useState<string | null>(null);

  const handleFeatureSelection = (event: MapEventOf<'click'>) => {
    const map = mapboxMap?.getMap();
    if (!map) return;
    const position = { x: event.originalEvent.clientX, y: event.originalEvent.clientY };
    const features = map.queryRenderedFeatures(event.point, { layers });
    const featuresFromDistinctItems = uniqBy(features, (feature) => feature.properties?.[featureKey]);

    if (featuresFromDistinctItems.length === 0) {
      setItems(null);
      setClickPosition(null);
      setSelectedFeatureKey(null);
      onClearSelection?.();
      return;
    }

    if (featuresFromDistinctItems.length === 1) {
      const feature = featuresFromDistinctItems[0];
      const featureId = feature.properties?.[featureKey];

      if (featureId === selectedFeatureKey) {
        setSelectedFeatureKey(null);
        onClearSelection?.();
        return;
      }

      setSelectedFeatureKey(featureId);
      onFeatureSelect(feature);
    } else {
      const items = Object.fromEntries(
        features
          .filter((feature) => feature.properties?.[featureLabelKey])
          .map((feature) => {
            const featureId = feature.properties?.[featureKey];
            const label = feature.properties?.[featureLabelKey];

            return [
              label,
              {
                label,
                shouldNotTranslate: true,
                onAction: () => {
                  setItems(null);
                  setClickPosition(null);
                  if (featureId === selectedFeatureKey) {
                    setSelectedFeatureKey(null);
                    onClearSelection?.();
                    return;
                  }
                  setSelectedFeatureKey(featureId);
                  onFeatureSelect(feature);
                },
              },
            ];
          }),
      );
      setItems(items);
    }
    setClickPosition(position);
  };

  useMapLayerMouseEvent('click', handleFeatureSelection);

  return { items, clickPosition };
}
